import { Fragment, useCallback, useMemo } from 'react';

import arrowDown from '~/assets/icons/arrow_down.svg';
import arrowUp from '~/assets/icons/arrow_up.svg';
import { playSound, Sound } from '~/assets/sounds';
import { rage, useInputAction } from '~/rage';
import { InputAction, PlayerVehicleSource } from '~/shared';

import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import { selectNext, selectPrev } from '../../redux/slices/vehicleListSlice';
import {
	StyledButton,
	StyledButtonContainer,
	StyledButtonIcon,
	StyledContainer,
	StyledIcon,
	StyledLabel,
} from './PanelControls.styled';

const PanelControls = () => {
	const dispatch = useLayoutDispatch();

	const selectedId = useLayoutSelector((state) => state.vehicleList.selectedId);
	const vehicles = useLayoutSelector((state) => state.vehicleList.vehicles);
	const workVehicles = useLayoutSelector(
		(state) => state.vehicleList.workVehicles,
	);

	const selectedVehicle = useMemo(
		() =>
			workVehicles.find((v) => v.id === selectedId) ??
			vehicles.find((v) => v.id === selectedId) ??
			null,
		[vehicles, workVehicles, selectedId],
	);

	const handleVehicleSpawn = useCallback(async () => {
		if (!selectedVehicle || selectedVehicle.isSpawned) return;
		if (rage.vehicles.isVehiclePending(selectedVehicle.id)) return;

		try {
			await rage.vehicles.spawnPlayerVehicle(selectedVehicle.id);
		} catch (error) {
			rage.notifications.showException(error);
		}
	}, [selectedVehicle]);

	const handleMoveVehicleToPlayer = useCallback(async () => {
		if (!selectedVehicle || !selectedVehicle.isSpawned) return;
		if (rage.vehicles.isVehiclePending(selectedVehicle.id)) return;

		try {
			await rage.vehicles.spawnPlayerVehicle(selectedVehicle.id);
		} catch (error) {
			rage.notifications.showException(error);
		}
	}, [selectedVehicle]);

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (selectedVehicle?.isSpawned) {
			handleMoveVehicleToPlayer().catch((err) =>
				rage.notifications.showException(err),
			);
		} else {
			handleVehicleSpawn().catch((err) =>
				rage.notifications.showException(err),
			);
		}
	});

	const handleVehicleDespawn = useCallback(async () => {
		if (!selectedVehicle || !selectedVehicle.isSpawned) return;
		if (selectedVehicle.source === PlayerVehicleSource.Job) return;
		if (rage.vehicles.isVehiclePending(selectedVehicle.id)) return;

		try {
			await rage.vehicles.destroyPlayerVehicle(selectedVehicle.id);
		} catch (error) {
			rage.notifications.showException(error);
		}
	}, [selectedVehicle]);

	useInputAction(InputAction.CommonMenuRemoveItem, () => {
		handleVehicleDespawn().catch((err) =>
			rage.notifications.showException(err),
		);
	});

	const handleVehicleLocked = useCallback(async () => {
		if (!selectedVehicle || !selectedVehicle.isSpawned) return;
		if (rage.vehicles.isVehiclePending(selectedVehicle.id)) return;

		try {
			await rage.vehicles.setPlayerVehicleLocked(
				selectedVehicle.id,
				!selectedVehicle.isLocked,
			);
		} catch (error) {
			rage.notifications.showException(error);
		}
	}, [selectedVehicle]);

	useInputAction(InputAction.PlayerVehiclesLockVehicle, () => {
		handleVehicleLocked().catch((err) => rage.notifications.showException(err));
	});

	const handleVehiclePrev = useCallback(() => {
		dispatch(selectPrev());
	}, []);

	useInputAction(InputAction.CommonMenuNavUp, () => {
		handleVehiclePrev();
	});

	const handleVehicleNext = useCallback(() => {
		dispatch(selectNext());
	}, []);

	useInputAction(InputAction.CommonMenuNavDown, () => {
		handleVehicleNext();
	});

	return (
		<StyledContainer>
			{!selectedId && (
				<StyledButtonContainer>
					<StyledButtonIcon>
						<StyledIcon $icon={arrowUp} />
					</StyledButtonIcon>
					<StyledButtonIcon>
						<StyledIcon $icon={arrowDown} />
					</StyledButtonIcon>
					<StyledLabel>Управление</StyledLabel>
				</StyledButtonContainer>
			)}
			{selectedVehicle && !selectedVehicle.isSpawned && (
				<StyledButtonContainer
					onClick={() => {
						handleVehicleSpawn();
						playSound(Sound.ClickButton);
					}}
					$clickable
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<StyledButton>Enter</StyledButton>
					<StyledLabel>Достать</StyledLabel>
				</StyledButtonContainer>
			)}
			{selectedVehicle && selectedVehicle.isSpawned && (
				<Fragment>
					<StyledButtonContainer
						onClick={() => {
							handleMoveVehicleToPlayer();
							playSound(Sound.ClickButton);
						}}
						$clickable
						onMouseEnter={() => playSound(Sound.LinkCursor)}
					>
						<StyledButton>Enter</StyledButton>
						<StyledLabel>К себе</StyledLabel>
					</StyledButtonContainer>
					{selectedVehicle.source !== PlayerVehicleSource.Job && (
						<StyledButtonContainer
							onClick={() => {
								handleVehicleDespawn();
								playSound(Sound.ClickButton);
							}}
							$clickable
							onMouseEnter={() => playSound(Sound.LinkCursor)}
						>
							<StyledButton>Del</StyledButton>
							<StyledLabel>Убрать</StyledLabel>
						</StyledButtonContainer>
					)}
				</Fragment>
			)}
			{selectedVehicle && selectedVehicle.isSpawned && (
				<StyledButtonContainer
					onClick={() => {
						handleVehicleLocked();
						playSound(Sound.ClickButton);
					}}
					$clickable
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<StyledButton>L</StyledButton>
					<StyledLabel>
						{selectedVehicle.isLocked ? 'Открыть' : 'Закрыть'}
					</StyledLabel>
				</StyledButtonContainer>
			)}
		</StyledContainer>
	);
};

export { PanelControls };
